import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Smartphone, KeyRound, ArrowLeft, Loader2, CheckCircle2, AlertCircle, ShieldCheck
} from 'lucide-react';
import { createRecaptchaVerifier, sendOTP, verifyOTP } from '../firebase/auth';
import { useAuth } from '../contexts/AuthContext';

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: 80px; /* Header Offset */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc; /* Slate-50 */
  position: relative;
  overflow: hidden;

  /* Abstract Background Shape */
  &::before {
    content: '';
    position: absolute;
    top: -10%;
    right: -5%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }
`;

const AuthCard = styled(motion.div)`
  background: white;
  width: 100%;
  max-width: 440px;
  padding: 48px 40px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 10;
  margin: 20px;

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;

  .icon-bg {
    width: 64px;
    height: 64px;
    background: #eef2ff; /* Indigo-50 */
    color: #4f46e5; /* Indigo-600 */
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.15);
  }

  h1 {
    font-size: 1.75rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
  }

  p {
    color: #64748b;
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
  margin-left: 4px;
`;

const PhoneWrapper = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  transition: all 0.2s ease;
  overflow: hidden;

  &:focus-within {
    border-color: #6366f1;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  }

  .prefix {
    background: #f1f5f9;
    padding: 14px 16px;
    font-weight: 600;
    color: #334155;
    border-right: 1px solid #cbd5e1;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  input {
    flex: 1;
    border: none;
    padding: 14px 16px;
    font-size: 1.1rem;
    color: #0f172a;
    font-weight: 500;
    outline: none;
    background: transparent;

    &::placeholder {
      color: #94a3b8;
    }
  }
`;

const OtpGrid = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 24px;

  input {
    width: 100%;
    height: 56px;
    border-radius: 12px;
    border: 1px solid #cbd5e1;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    background: white;
    transition: all 0.2s ease;

    &:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
      outline: none;
      transform: translateY(-2px);
    }
  }
`;

const ActionButton = styled.button`
  width: 100%;
  background: #4f46e5;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover:not(:disabled) {
    background: #4338ca;
    transform: translateY(-1px);
    box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.3);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    opacity: 0.8;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  padding: 8px 0;
  transition: color 0.2s;

  &:hover {
    color: #0f172a;
  }
`;

const ResendText = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 0.9rem;
  color: #64748b;

  button {
    background: none;
    border: none;
    color: #4f46e5;
    font-weight: 600;
    cursor: pointer;
    margin-left: 4px;

    &:hover:not(:disabled) {
      text-decoration: underline;
    }

    &:disabled {
      color: #94a3b8;
      cursor: not-allowed;
    }
  }
`;

const AlertBox = styled(motion.div)`
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 24px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &.error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fee2e2;
  }

  &.success {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #dcfce7;
  }
`;

// --- COMPONENT ---

const Login = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    // State
    const [step, setStep] = useState('phone'); // 'phone' | 'otp'
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [resendTimer, setResendTimer] = useState(0);

    const otpInputsRef = useRef([]);

    // Redirect if logged in
    useEffect(() => {
        if (isAuthenticated) navigate('/dashboard');
    }, [isAuthenticated, navigate]);

    // Recaptcha Init
    useEffect(() => {
        const initRecaptcha = () => {
            if (!window.recaptchaVerifier && document.getElementById('recaptcha-container')) {
                try {
                    window.recaptchaVerifier = createRecaptchaVerifier('recaptcha-container');
                } catch (err) {
                    console.error('Recaptcha Init Error:', err);
                }
            }
        };
        initRecaptcha();
        // Re-attempt just in case of race conditions
        const timer = setTimeout(initRecaptcha, 500);
        return () => clearTimeout(timer);
    }, [step]);

    // Resend Timer
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    // --- Handlers ---

    const handlePhoneChange = (e) => {
        const val = e.target.value.replace(/\D/g, ''); // Only numbers
        setPhoneNumber(val);
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setError('');

        // Basic validation
        if (phoneNumber.length !== 10) {
            setError('Please enter a valid 10-digit mobile number.');
            return;
        }

        setLoading(true);
        try {
            const formattedPhone = `+91${phoneNumber}`;
            const result = await sendOTP(formattedPhone, window.recaptchaVerifier);
            setConfirmationResult(result);
            setStep('otp');
            setResendTimer(45);
            setSuccess('Code sent successfully!');
        } catch (err) {
            console.error(err);
            setError('Failed to send OTP. Please try again or check your connection.');
            // Reset recaptcha if failed
            if (window.recaptchaVerifier) window.recaptchaVerifier.clear();
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setError('Please enter the complete 6-digit code.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await verifyOTP(confirmationResult, otpString);
            setSuccess('Verified! Redirecting...');
            setTimeout(() => navigate('/dashboard'), 1000);
        } catch (err) {
            setError('Invalid code. Please try again.');
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (isNaN(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1); // Only take last char
        setOtp(newOtp);

        // Auto focus next
        if (value && index < 5) otpInputsRef.current[index + 1]?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputsRef.current[index - 1]?.focus();
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) return;
        setResendTimer(60);
        setError('');
        setSuccess('');

        try {
            const formattedPhone = `+91${phoneNumber}`;
            const result = await sendOTP(formattedPhone, window.recaptchaVerifier);
            setConfirmationResult(result);
            setSuccess('Code resent!');
        } catch (err) {
            setError('Failed to resend. Please try later.');
        }
    };

    return (
        <PageContainer>
            <AuthCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* HEADER */}
                {step === 'otp' && (
                    <BackLink onClick={() => setStep('phone')}>
                        <ArrowLeft size={16} /> Change Number
                    </BackLink>
                )}

                <Header>
                    <motion.div
                        className="icon-bg"
                        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}
                    >
                        {step === 'phone' ? <Smartphone size={32} /> : <KeyRound size={32} />}
                    </motion.div>
                    <h1>{step === 'phone' ? 'Partner Login' : 'Verify Identity'}</h1>
                    <p>
                        {step === 'phone'
                            ? 'Enter your mobile number to access your dashboard.'
                            : `Enter the code sent to +91 ${phoneNumber}`}
                    </p>
                </Header>

                {/* ALERTS */}
                <AnimatePresence>
                    {error && (
                        <AlertBox
                            className="error"
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        >
                            <AlertCircle size={18} /> {error}
                        </AlertBox>
                    )}
                    {success && (
                        <AlertBox
                            className="success"
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        >
                            <CheckCircle2 size={18} /> {success}
                        </AlertBox>
                    )}
                </AnimatePresence>

                {/* PHONE FORM */}
                {step === 'phone' && (
                    <form onSubmit={handleSendOtp}>
                        <InputGroup>
                            <Label>Mobile Number</Label>
                            <PhoneWrapper>
                                <div className="prefix"><ShieldCheck size={16} /> +91</div>
                                <input
                                    type="tel"
                                    placeholder="98765 43210"
                                    value={phoneNumber}
                                    onChange={handlePhoneChange}
                                    maxLength="10"
                                    autoFocus
                                />
                            </PhoneWrapper>
                        </InputGroup>

                        <ActionButton type="submit" disabled={loading || phoneNumber.length < 10}>
                            {loading ? <Loader2 className="spin" /> : 'Get OTP'}
                        </ActionButton>
                    </form>
                )}

                {/* OTP FORM */}
                {step === 'otp' && (
                    <form onSubmit={handleVerifyOtp}>
                        <InputGroup>
                            <Label style={{ textAlign: 'center', marginBottom: '16px' }}>6-Digit Security Code</Label>
                            <OtpGrid>
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={el => otpInputsRef.current[idx] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleOtpChange(idx, e.target.value)}
                                        onKeyDown={e => handleOtpKeyDown(idx, e)}
                                        autoFocus={idx === 0}
                                    />
                                ))}
                            </OtpGrid>
                        </InputGroup>

                        <ActionButton type="submit" disabled={loading || otp.join('').length < 6}>
                            {loading ? <Loader2 className="spin" /> : 'Verify & Login'}
                        </ActionButton>

                        <ResendText>
                            Didn't receive code?
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={resendTimer > 0}
                            >
                                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Now'}
                            </button>
                        </ResendText>
                    </form>
                )}

                {/* Invisible Recaptcha */}
                <div id="recaptcha-container"></div>
            </AuthCard>
        </PageContainer>
    );
};

export default Login;